import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAeroDomain } from '../../utils/domainUtils';
import { getSessionLimit } from '../../utils/userTestingLimits';
import AeroShellSidebar from './AeroShellSidebar';
import '../../styles/userTesting/index.css';
import '../../styles/aero-ut-theme.css';
import './AeroSidebar.css';

const PLAN_LABELS = { regular: 'Free plan', basic: 'Basic plan', solo: 'Solo plan', pro: 'Pro plan' };

const time = (t) => (t.updatedAt || t.createdAt || new Date(0)).getTime();

/**
 * AeroSidebar: the shared sidebar for every Aero product page, mounted once in
 * AeroLayout. It works out the active item from the route and hands the shell
 * data (useAeroShellData, read in AeroLayout) to the v3 view, AeroShellSidebar.
 * Collapse state, the mobile menu button and the backdrop stay in AeroLayout,
 * unchanged.
 */
function AeroSidebar({ subscriptionType = 'regular', isAdmin = false, collapsed = false, onToggle, onNavClose, shell = {} }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const aero = isAeroDomain();

  const active = useMemo(() => {
    if (pathname.startsWith('/user-testing')) return 'tests';
    if (/^(\/[^/]+)?\/sites(\/|$)/.test(pathname)) return 'sites';
    if (/^(\/[^/]+)?\/users$/.test(pathname)) return 'users';
    if (aero && pathname === '/') return 'home';
    return null;
  }, [pathname, aero]);

  const openTestId = useMemo(() => {
    const m = pathname.match(/^\/user-testing\/([^/]+)/);
    return m ? m[1] : null;
  }, [pathname]);

  const { tests, sites, sessionsUsed, sessionInfo, account = {} } = shell;

  const recentTests = useMemo(() => {
    if (!tests) return [];
    return tests
      .filter((t) => !t.draft)
      .sort((a, b) => time(b) - time(a))
      .slice(0, 5)
      .map((t) => ({
        id: t.id,
        name: t.title,
        state: t.status === 'completed' ? 'closed' : t.status === 'draft' ? 'draft' : 'open',
        sessions: sessionInfo && sessionInfo[t.id] ? sessionInfo[t.id].total : null,
        newCount: sessionInfo && sessionInfo[t.id] ? sessionInfo[t.id].new : 0,
      }));
  }, [tests, sessionInfo]);

  const counts = {
    sites: sites ? sites.length : null,
    tests: tests ? tests.filter((t) => !t.draft).length : null,
  };

  const plan = {
    label: PLAN_LABELS[subscriptionType] || PLAN_LABELS.regular,
    used: sessionsUsed,
    limit: getSessionLimit(subscriptionType),
  };

  const go = (path) => {
    if (typeof window !== 'undefined' && window.innerWidth <= 1024 && onNavClose) {
      onNavClose();
    }
    navigate(path);
  };

  return (
    <AeroShellSidebar
      collapsed={collapsed}
      active={active}
      openTestId={openTestId}
      showHome={aero}
      homePath={aero ? '/' : '/user-testing'}
      counts={counts}
      recentTests={recentTests}
      plan={plan}
      account={account}
      isAdmin={isAdmin}
      onNavigate={go}
      onToggle={onToggle}
      onPlanClick={() => go('/account')}
    />
  );
}

export default AeroSidebar;
